import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Box, Typography, CircularProgress, Container } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, already_used, error
  const [message, setMessage] = useState("Verifying your email...");
  const [countdown, setCountdown] = useState(5);

  const verifyRef = useRef(null);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");
      // Properly decode email to handle + signs and other special characters
      // Replace spaces with + (URLs sometimes decode + as space)
      const emailParam = searchParams.get("email");
      const email = emailParam
        ? decodeURIComponent(emailParam).replace(/ /g, "+")
        : "";

      if (!token || !email) {
        setStatus("error");
        setMessage("Invalid verification link. Missing token or email.");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/auth/verify-email-link`,
          {
            params: { token, email },
            withCredentials: true,
          },
        );

        if (response.data.success) {
          if (response.data.alreadyVerified) {
            // Link was already used - show informational state (not error, but clear it was used)
            setStatus("already_used");
            setMessage(
              "This verification link has already been used. Your email was verified successfully.",
            );
          } else {
            // Fresh verification - show success
            setStatus("success");
            setMessage(
              "Your email has been verified. You can now close this tab and return to the registration page.",
            );
          }
        } else {
          setStatus("error");
          setMessage(
            response.data.message || "Verification failed. Please try again.",
          );
        }
      } catch (error) {
        setStatus("error");
        setMessage(
          error.response?.data?.message ||
            "Verification failed. The link may have expired. Please request a new verification email.",
        );
      }
    };

    if (!verifyRef?.current) {
      verifyRef.current = true;
      verifyEmail();
    }
  }, [searchParams, navigate]);

  // Countdown for auto-close (for both success and already_used states)
  useEffect(() => {
    if (status !== "success" && status !== "already_used") return;
    if (countdown <= 0) {
      window.close();
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [status, countdown]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          textAlign: "center",
          padding: 3,
        }}
      >
        {status === "verifying" && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Verifying your email...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your email address.
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleIcon
              sx={{ fontSize: 80, color: "success.main", mb: 2 }}
            />
            <Typography
              variant="h4"
              gutterBottom
              color="success.main"
              fontWeight="bold"
            >
              Email Verified!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The registration page will automatically complete your signup.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This tab will close in {countdown} seconds...
            </Typography>
          </>
        )}

        {status === "already_used" && (
          <>
            <InfoIcon sx={{ fontSize: 80, color: "info.main", mb: 2 }} />
            <Typography
              variant="h4"
              gutterBottom
              color="info.main"
              fontWeight="bold"
            >
              Link Already Used
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can safely close this tab and return to the registration page.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This tab will close in {countdown} seconds...
            </Typography>
          </>
        )}

        {status === "error" && (
          <>
            <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom color="error.main">
              Verification Failed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please return to the registration page and request a new
              verification email.
            </Typography>
          </>
        )}
      </Box>
    </Container>
  );
};

export default VerifyEmailPage;
